/**
 * Redux slice — Tasks V2 Migration v4 plan PR7.
 *
 * Backs the DryRunReminderPreviewPanel cockpit panel. The slice fetches
 * paginated rows from the BE endpoint introduced in PR3:
 *
 *     GET /api/v1/management/tasks-migration/runs/{run_id}/preview/
 *
 * The endpoint accepts query params:
 *   limit, offset                                 -- pagination
 *   filter_warning_code                            -- string prefix
 *   filter_template_source = system|practice|group
 *   filter_has_skipped_items = true|false
 *   filter_has_warnings      = true|false          -- chip-driven
 *
 * The endpoint returns the standard paginator shape extended with a
 * ``meta`` block carrying bucket counts aggregated over the FILTERED
 * queryset (warnings_count, skipped_count, treatment_scoped_count,
 * archived_count). The cockpit rail renders chip counts from these
 * instead of computing them client-side off the visible page -- at
 * 3000+ rows the page-local counts were misleading.
 *
 * 404 from the endpoint means ``TASKS_V2_MIGRATION_PREVIEW_V2`` is OFF
 * on the BE -- we set ``featureAvailable=false`` and the panel renders
 * null. Same convention as PR2.5's TemplateSelectionPanel.
 *
 * State is keyed by runId so multiple cockpits in the same session
 * (rare, but possible via Storybook / dev fixtures) don't bleed.
 */
import { createSlice } from '@reduxjs/toolkit';

import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';


const BASE = '/api/v1/management/tasks-migration';


const initialState = {
    // Per-run buffer: ``byRun[runId] = { rows, count, offset, limit,
    // filters, loading, error }``.
    byRun: {},
    // Flips to false on a 404; panel renders null.
    featureAvailable: true,
};


const slice = createSlice({
    name: 'tasksV2DryRunPreview',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { runId, loading } = action.payload;
            if (!state.byRun[runId]) {
                state.byRun[runId] = _emptyRunBuffer();
            }
            state.byRun[runId].loading = loading;
        },
        setRows(state, action) {
            const {
                runId, rows, count, meta, offset, limit, filters,
            } = action.payload;
            if (!state.byRun[runId]) {
                state.byRun[runId] = _emptyRunBuffer();
            }
            const buf = state.byRun[runId];
            buf.rows = rows;
            buf.count = count;
            // Preserve previous meta when the BE response omits it (e.g.
            // an old BE during a staggered deploy). The rail then falls
            // back to its previous chip counts rather than zeroing them.
            if (meta) buf.meta = meta;
            buf.offset = offset;
            buf.limit = limit;
            if (filters) buf.filters = filters;
            buf.error = null;
        },
        setError(state, action) {
            const { runId, error } = action.payload;
            if (!state.byRun[runId]) {
                state.byRun[runId] = _emptyRunBuffer();
            }
            state.byRun[runId].error = error;
        },
        setFeatureUnavailable(state) {
            state.featureAvailable = false;
        },
        clearForRun(state, action) {
            const runId = action.payload;
            delete state.byRun[runId];
        },
        clearAll(state) {
            state.byRun = {};
            state.featureAvailable = true;
        },
    },
});


function _emptyRunBuffer() {
    return {
        rows: [],
        count: 0,
        // Bucket counts aggregated by the BE over the FILTERED queryset.
        // Backs the rail's quick-filter chip labels.
        meta: {
            warnings_count: 0,
            skipped_count: 0,
            treatment_scoped_count: 0,
            archived_count: 0,
        },
        offset: 0,
        limit: 25,
        filters: {
            warning_code: null,
            template_source: null,
            has_skipped_items: false,
            has_warnings: false,
        },
        loading: false,
        error: null,
    };
}


export default slice.reducer;
export const {
    clearForRun,
    clearAll,
} = slice.actions;


/**
 * Fetch a page of dry-run preview rows for ``runId``.
 *
 * Args:
 *   runId       -- str (UUID)
 *   opts        -- {
 *                    limit, offset,
 *                    filter_warning_code, filter_template_source,
 *                    filter_has_skipped_items,
 *                  }
 *
 * Behaviour:
 *   * 200: stores the page in ``byRun[runId]`` (replacing any existing
 *     rows so pagination jumps don't accumulate stale data).
 *   * 404: flips ``featureAvailable=false``; panel renders null.
 *   * other: stores ``error`` for the run; panel shows a soft "couldn't
 *     load" stub without blowing up the cockpit.
 */
export function fetchDryRunPreviewPage(runId, opts = {}) {
    return async () => {
        if (!runId) return null;

        const limit = opts.limit ?? 25;
        const offset = opts.offset ?? 0;
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (opts.filter_warning_code) {
            params.set('filter_warning_code', opts.filter_warning_code);
        }
        if (opts.filter_template_source) {
            params.set('filter_template_source', opts.filter_template_source);
        }
        if (opts.filter_has_skipped_items) {
            params.set('filter_has_skipped_items', 'true');
        }
        if (opts.filter_has_warnings) {
            params.set('filter_has_warnings', 'true');
        }

        dispatch(slice.actions.setLoading({ runId, loading: true }));
        try {
            const url = `${BASE}/runs/${runId}/preview/?${params.toString()}`;
            const response = await callAdminApi(url);

            if (response.status === 404) {
                dispatch(slice.actions.setFeatureUnavailable());
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setError({
                    runId,
                    error: `HTTP ${response.status}`,
                }));
                return null;
            }
            const data = response.data || {};
            dispatch(slice.actions.setRows({
                runId,
                rows: Array.isArray(data.results) ? data.results : [],
                count: typeof data.count === 'number' ? data.count : 0,
                meta: data.meta || null,
                offset,
                limit,
                filters: {
                    warning_code: opts.filter_warning_code || null,
                    template_source: opts.filter_template_source || null,
                    has_skipped_items: !!opts.filter_has_skipped_items,
                    has_warnings: !!opts.filter_has_warnings,
                },
            }));
            return data;
        } catch (error) {
            dispatch(slice.actions.setError({
                runId,
                error: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setLoading({ runId, loading: false }));
        }
    };
}
